// Where visitors come from (the `ub_src` cookie), for visitors who land on the portal directly (e.g. a link in
// ChatGPT to /register). Same cookie contract as ultimo-bots-webpage/lib/sourceTracking.js — keep them in sync:
// FIRST touch never replaced, LAST non-direct touch, each with referrer host, utm_*, ad click id type and
// landing path. The backend reads it at signup and Wix install (src/controller/acquisition.py).
//
// Independent of ub_attrib (utils/adsTracking.js, Google Ads). Everything swallows its own errors.

const COOKIE = 'ub_src';
const MAX_AGE = 60 * 60 * 24 * 180;
const UTM = { utm_source: 's', utm_medium: 'm', utm_campaign: 'c', utm_term: 't', utm_content: 'x' };
const CLICK_IDS = ['gclid', 'gbraid', 'wbraid', 'msclkid', 'fbclid', 'ttclid', 'li_fat_id', 'twclid', 'rdt_cid', 'epik'];
const CAPS = { s: 120, m: 120, c: 160, t: 160, x: 160, r: 120, l: 300, k: 20 };

function readCookie(name = COOKIE) {
  try {
    const raw = document.cookie.split('; ').find((c) => c.startsWith(name + '='));
    if (!raw) return null;
    const data = JSON.parse(decodeURIComponent(raw.slice(name.length + 1)));
    return data && typeof data === 'object' ? data : null;
  } catch (e) {
    return null;
  }
}

function ownHost(host) {
  return !host || host === window.location.hostname || host === 'ultimo-bots.com' || host.endsWith('.ultimo-bots.com');
}

// Call once at boot. Reads the referrer and the landing URL's utm_* / ad click id.
export function captureSource() {
  try {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const touch = {};
    Object.keys(UTM).forEach((key) => {
      const v = params.get(key);
      if (v) touch[UTM[key]] = v.slice(0, CAPS[UTM[key]]);
    });
    const click = CLICK_IDS.find((k) => params.get(k));
    if (click) touch.k = click;
    let ref = '';
    try {
      ref = document.referrer ? new URL(document.referrer).hostname.toLowerCase() : '';
    } catch (e) {
      ref = '';
    }
    if (ref && !ownHost(ref)) touch.r = ref.replace(/^www\./, '').slice(0, CAPS.r);
    const hasTouch = Object.keys(touch).length > 0;
    if (hasTouch) {
      touch.l = window.location.pathname.slice(0, CAPS.l);
      touch.ts = new Date().toISOString();
    }
    const prev = readCookie() || {};
    const next = { v: 1, ft: prev.ft, lt: prev.lt };
    if (!next.ft) next.ft = hasTouch ? touch : { s: '(direct)', l: window.location.pathname.slice(0, CAPS.l), ts: new Date().toISOString() };
    if (hasTouch) next.lt = touch;
    if (JSON.stringify(next) === JSON.stringify({ v: 1, ft: prev.ft, lt: prev.lt })) return;
    writeCookie(COOKIE, next, MAX_AGE);
  } catch (e) {
    // never break the portal
  }
}

function writeCookie(name, value, maxAge) {
  let cookie = `${name}=${encodeURIComponent(JSON.stringify(value))}; Max-Age=${maxAge}; Path=/; SameSite=Lax`;
  if (window.location.hostname.endsWith('ultimo-bots.com')) cookie += '; Domain=.ultimo-bots.com';
  if (window.location.protocol === 'https:') cookie += '; Secure';
  document.cookie = cookie;
}

// Partner links (`?ref=<code>`, the referral program): the `ub_ref` cookie {c, ts} for 90 days, the LAST link
// wins, plus one click report. Same contract as ultimo-bots-webpage/lib/sourceTracking.js; the backend reads the
// cookie at signup (src/controller/referral.py). A reload of the same link within 30 minutes is not a new click.
const REF_COOKIE = 'ub_ref';
const REF_MAX_AGE = 60 * 60 * 24 * 90;
const REF_CODE = /^[a-z0-9]{6,16}$/;

export function captureReferral() {
  try {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const code = (new URLSearchParams(window.location.search).get('ref') || '').trim().toLowerCase();
    if (!REF_CODE.test(code)) return;
    const prev = readCookie(REF_COOKIE);
    if (prev && prev.c === code && Date.now() - Date.parse(prev.ts) < 30 * 60 * 1000) return;
    writeCookie(REF_COOKIE, { c: code, ts: new Date().toISOString() }, REF_MAX_AGE);
    const base = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
    if (base) {
      fetch(`${base}/referral/click`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code }), keepalive: true })
        .catch(() => {});
    }
  } catch (e) {
    // never break the portal
  }
}

// Which portal flow sent the visitor to the register page: ChatGPT / MCP consent, a Wix app, an invite.
export function signupContext() {
  try {
    const r = new URLSearchParams(window.location.search).get('returnTo') || '';
    if (r.startsWith('/mcp/') || r === '/mcp-connections') return 'mcp';
    const wix = r.match(/^\/wix\/([a-z0-9-]{1,40})\/start/);
    if (wix) return `wix_product:${wix[1]}`;
    if (r.startsWith('/accept-invite/')) return 'invite';
  } catch (e) {
    // ignore
  }
  return undefined;
}

// sign_up to the dataLayer (Tag Manager forwards it to GA4). Never throws.
export function pushSignupEvent(method, context) {
  try {
    if (window.__UB_NO_TELEMETRY__) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'sign_up', method, signup_context: context || 'portal' });
  } catch (e) {
    // never break the signup
  }
}
